import { useNavigate } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import {
  Wheat, ShieldCheck, TrendingDown, Ambulance, Store, Users,
  Sprout, HeartPulse, LifeBuoy, Download, ArrowRight,
} from "lucide-react";

const PARTNER_IMG = "https://images.pexels.com/photos/6646899/pexels-photo-6646899.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const areas = [
  { icon: Wheat, t: "Food Security" },
  { icon: ShieldCheck, t: "Social Protection" },
  { icon: TrendingDown, t: "Poverty Reduction" },
  { icon: Ambulance, t: "Humanitarian Response" },
  { icon: Store, t: "SME Development" },
  { icon: Users, t: "Women's Empowerment" },
  { icon: Sprout, t: "Youth Programmes" },
  { icon: HeartPulse, t: "Community Resilience" },
  { icon: LifeBuoy, t: "Emergency Assistance" },
];

export default function DevelopmentPartners() {
  const navigate = useNavigate();
  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">For Development Agencies</span>
            <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">Deliver measurable impact through national digital benefit infrastructure.</h1>
            <p className="mt-5 text-emerald-200/80 max-w-xl leading-relaxed">TMF gives development agencies, philanthropists and governments a secure, auditable rail to deliver benefits directly to verified beneficiaries — with full traceability from funding to redemption to impact report.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="/docs/tmf-institutional-brief.pdf" download data-testid="download-brief-btn"
                className="inline-flex items-center bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-12 px-6 text-sm font-medium transition-colors">
                <Download className="w-4 h-4 mr-2" /> Download Institutional Programme Brief
              </a>
              <Button data-testid="partners-start-btn" onClick={() => navigate("/signup")} variant="outline" className="rounded-full h-12 px-6 border-emerald-700 text-emerald-100 hover:bg-emerald-900">Partner with TMF <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={PARTNER_IMG} alt="Development partners distributing aid" className="rounded-2xl shadow-2xl object-cover w-full h-80 border-4 border-emerald-800/40" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">How TMF Can Support Your Mandate</span>
        <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-emerald-950 tracking-tight max-w-2xl">One infrastructure, many programme mandates.</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <div key={a.t} data-testid={`partner-area-${a.t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} className="flex items-center gap-4 rounded-2xl border border-emerald-100 bg-white p-5 hover:shadow-md hover:border-emerald-200 transition-all">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 flex items-center justify-center"><a.icon className="w-6 h-6 text-emerald-600" /></div>
              <span className="font-display font-semibold text-emerald-950">{a.t}</span>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 lg:p-10 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-2xl">Ready to review the full brief?</h3>
            <p className="mt-2 text-emerald-50/90 text-sm max-w-lg">Everything an institutional partner needs — the model, safeguards, reporting and reach.</p>
          </div>
          <a href="/docs/tmf-institutional-brief.pdf" download data-testid="download-brief-btn-2" className="inline-flex items-center bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-12 px-6 text-sm font-medium transition-colors shrink-0">
            <Download className="w-4 h-4 mr-2" /> Download Brief (PDF)
          </a>
        </div>
      </section>
    </PublicShell>
  );
}
